import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";

export const Route = createFileRoute("/redeem")({
  component: RedeemPage,
  validateSearch: (search): { email?: string } => ({
    email: typeof search.email === "string" ? search.email : undefined,
  }),
});

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string) ?? "https://api.stockclerk.app";
const TENANT_SLUG = "the-little-green-hound-pantry";
const REDEEM_URL = `${API_BASE}/api/loyalty/redeem`;

type Tier = "small" | "medium" | "large";

const TIERS: Array<{ id: Tier; pts: number; label: string; valuePence: number }> = [
  { id: "small",  pts: 350,  label: "£10 off", valuePence: 1000 },
  { id: "medium", pts: 850,  label: "£25 off", valuePence: 2500 },
  { id: "large",  pts: 1500, label: "£50 off", valuePence: 5000 },
];

type SuccessState = {
  kind: "success";
  code: string;
  valuePence: number;
  pointsDeducted: number;
  newBalance: number;
};

type View =
  | { kind: "form" }
  | SuccessState
  | { kind: "error"; title: string; message: string; recoverable: boolean };

const EMAIL_RE = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;

function RedeemPage() {
  const search = Route.useSearch();
  const initialEmail = search.email ?? "";

  const [email, setEmail] = useState(initialEmail);
  const [tier, setTier] = useState<Tier>("small");
  const [submitting, setSubmitting] = useState(false);
  const [view, setView] = useState<View>({ kind: "form" });

  const selectedTier = useMemo(() => TIERS.find(t => t.id === tier)!, [tier]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;

    const trimmed = email.trim().toLowerCase();
    if (!EMAIL_RE.test(trimmed)) {
      setView({
        kind: "error",
        title: "Email looks off",
        message: "Use the email you signed up with.",
        recoverable: true,
      });
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(REDEEM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenantSlug: TENANT_SLUG,
          email: trimmed,
          tier,
        }),
      });

      if (res.status === 200) {
        const data = (await res.json()) as {
          code: string;
          amount_pence: number;
          points_deducted: number;
          new_balance: number;
        };
        setView({
          kind: "success",
          code: data.code,
          valuePence: data.amount_pence,
          pointsDeducted: data.points_deducted,
          newBalance: data.new_balance,
        });
        return;
      }

      const payload = (await res.json().catch(() => ({}))) as {
        error?: string;
        balance?: number;
        required?: number;
        next_allowed_at?: string;
      };

      if (res.status === 404 && payload.error === "not_enrolled") {
        setView({
          kind: "error",
          title: "We don't recognise that email",
          message:
            "Sign up for Hound Club first — your wallet pass is needed before you can redeem.",
          recoverable: true,
        });
        return;
      }
      if (res.status === 409 && payload.error === "insufficient_points") {
        const have = payload.balance ?? 0;
        const need = payload.required ?? selectedTier.pts;
        setView({
          kind: "error",
          title: "Not quite enough points yet",
          message: `You have ${have} points — you need ${need} for ${selectedTier.label}.`,
          recoverable: true,
        });
        return;
      }
      if (res.status === 409 && payload.error === "cooloff_active") {
        const when = payload.next_allowed_at
          ? new Date(payload.next_allowed_at).toLocaleString("en-GB", {
              dateStyle: "medium",
              timeStyle: "short",
            })
          : "shortly";
        setView({
          kind: "error",
          title: "One redemption per day",
          message: `Your next redemption is available from ${when}.`,
          recoverable: false,
        });
        return;
      }
      if (res.status === 503) {
        setView({
          kind: "error",
          title: "Redemption isn't switched on yet",
          message:
            "We're still finishing the redemption flow. We'll email you when it's live.",
          recoverable: false,
        });
        return;
      }

      setView({
        kind: "error",
        title: "Something went wrong",
        message:
          payload.error === "wix_unavailable"
            ? "Our loyalty provider didn't respond. Try again in a minute."
            : "Please try again, or show your pass to staff and they'll help.",
        recoverable: true,
      });
    } catch (err) {
      setView({
        kind: "error",
        title: "No connection",
        message: "Check your signal and try again.",
        recoverable: true,
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen" style={{ background: "#f4f1ec" }}>
      <main className="mx-auto max-w-[440px] px-5 py-8 text-neutral-800">
        <header className="mb-6">
          <h1 className="text-2xl font-semibold">Redeem points</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Pick a reward, then show the code to staff at the till.
          </p>
        </header>

        {view.kind === "form" && (
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium">
                Your email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                inputMode="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-base focus:border-neutral-500 focus:outline-none"
              />
            </div>

            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Choose a reward</legend>
              {TIERS.map((t) => (
                <label
                  key={t.id}
                  className={`flex cursor-pointer items-center justify-between rounded-lg border px-4 py-3 ${
                    tier === t.id
                      ? "border-neutral-800 bg-white"
                      : "border-neutral-300 bg-white/50"
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <input
                      type="radio"
                      name="tier"
                      value={t.id}
                      checked={tier === t.id}
                      onChange={() => setTier(t.id)}
                      className="h-4 w-4"
                    />
                    <span className="font-medium">{t.label}</span>
                  </span>
                  <span className="text-sm text-neutral-500">{t.pts} pts</span>
                </label>
              ))}
            </fieldset>

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg bg-neutral-900 px-4 py-3 text-base font-medium text-white disabled:opacity-50"
              style={{ background: "rgb(67, 87, 63)" }}
            >
              {submitting ? "Redeeming…" : `Redeem ${selectedTier.label}`}
            </button>

            <p className="text-xs text-neutral-500">
              By tapping Redeem you'll be deducted {selectedTier.pts} points
              immediately. Show the code to staff at the till for {selectedTier.label}.
            </p>
          </form>
        )}

        {view.kind === "success" && (
          <section className="space-y-5">
            <div className="rounded-2xl bg-white p-6 text-center shadow-sm">
              <p className="text-sm uppercase tracking-wide text-neutral-500">
                Show this at the till
              </p>
              <p
                className="my-3 font-mono text-4xl tracking-widest"
                style={{ color: "rgb(67, 87, 63)" }}
              >
                {view.code}
              </p>
              <p className="text-base font-medium">
                £{(view.valuePence / 100).toFixed(2)} off your purchase
              </p>
            </div>

            <div className="rounded-lg border border-neutral-200 bg-white/70 p-4 text-sm">
              <p>
                <span className="text-neutral-500">Points deducted:</span>{" "}
                <strong>{view.pointsDeducted}</strong>
              </p>
              <p className="mt-1">
                <span className="text-neutral-500">New balance:</span>{" "}
                <strong>{view.newBalance.toLocaleString("en-GB")}</strong>
              </p>
            </div>

            <p className="text-xs text-neutral-500">
              The discount must be applied during this visit. Codes expire when
              you walk out.
            </p>
          </section>
        )}

        {view.kind === "error" && (
          <section className="space-y-4">
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-4">
              <p className="font-medium text-rose-900">{view.title}</p>
              <p className="mt-1 text-sm text-rose-900/80">{view.message}</p>
            </div>
            {view.recoverable && (
              <button
                type="button"
                onClick={() => setView({ kind: "form" })}
                className="w-full rounded-lg border border-neutral-300 bg-white px-4 py-3 text-base font-medium"
              >
                Try again
              </button>
            )}
          </section>
        )}

        <footer className="mt-10 text-center text-xs text-neutral-500">
          <Link to="/" className="underline">
            ← Back to Hound Club
          </Link>
        </footer>
      </main>
    </div>
  );
}
